import {
  AggFunction,
  BaseMetricDraft,
  DerivedMetricDraft,
  MetricAdvisor,
  MetricSuggestion,
  MetricSuggestionInput,
} from './types';
import { isIdentifierColumn, isNumericColumn, toSnakeCase } from './autoInference';

/**
 * Heuristic metric advisor (client-side, deterministic).
 *
 * Reads the user's businessContext + per-column columnNotes + parsed columns
 * and proposes base & derived metrics using keyword rules.
 * No network, no AWS, no LLM.
 */

// ─────────────────────────────────────────────────────────────────────────
// Constants — keyword -> aggregation rules (case-insensitive)
// ─────────────────────────────────────────────────────────────────────────

// All keyword patterns are word-boundary anchored on whitespace/underscore
// (?:^|[\s_])…(?:$|[\s_]) so they match whole tokens in the concatenated
// "note colName businessContext" signal. Without anchors, substrings cause
// misclassification — e.g. "rate" inside "generates", "cost" inside "Costco",
// "spend" inside "suspend". Consistent with MIN_KEYWORDS/MAX_KEYWORDS below.

/** Keywords that signal SUM aggregation. */
const SUM_KEYWORDS = /(?:^|[\s_])(?:revenue|sales|gmv|cost|cogs|spend|expense|units|quantity|qty)(?:$|[\s_])/i;

/** Keywords specifically for units/quantity (trigger a COUNT warning). */
const UNITS_KEYWORDS = /(?:^|[\s_])(?:units|quantity|qty)(?:$|[\s_])/i;

/** Keywords that signal AVG aggregation. */
const AVG_KEYWORDS = /(?:^|[\s_])(?:avg|average|mean|rate|ratio)(?:$|[\s_])/i;

/** Keywords that signal MIN aggregation. */
const MIN_KEYWORDS = /(?:^|[\s_])min(?:$|[\s_])|minimum/i;

/** Keywords that signal MAX aggregation. */
const MAX_KEYWORDS = /(?:^|[\s_])max(?:$|[\s_])|maximum/i;

/** Revenue-like keywords for derived metric detection. */
const REVENUE_KEYWORDS = /(?:^|[\s_])(?:revenue|sales|gmv)(?:$|[\s_])/i;

/** Cost-like keywords for derived metric detection. */
const COST_KEYWORDS = /(?:^|[\s_])(?:cost|cogs|spend|expense)(?:$|[\s_])/i;

// ─────────────────────────────────────────────────────────────────────────
// Core logic
// ─────────────────────────────────────────────────────────────────────────

/**
 * Determines the aggregation function for a column by scanning its note,
 * column name, and businessContext for keywords (in that priority order).
 */
function resolveAgg(colName: string, note: string, context: string): AggFunction {
  // Concatenate signal sources in priority order for keyword search
  const signal = `${note} ${colName} ${context}`;

  if (AVG_KEYWORDS.test(signal)) return 'AVG';
  if (MIN_KEYWORDS.test(signal)) return 'MIN';
  if (MAX_KEYWORDS.test(signal)) return 'MAX';
  if (SUM_KEYWORDS.test(signal)) return 'SUM';
  // Default: numeric with no keyword signal -> SUM
  return 'SUM';
}

/**
 * Checks if a column matches a keyword pattern given its signal text.
 */
function matchesKeyword(pattern: RegExp, colName: string, note: string, context: string): boolean {
  return pattern.test(`${note} ${colName} ${context}`);
}

export const heuristicAdvisor: MetricAdvisor = {
  async suggest(input: MetricSuggestionInput): Promise<MetricSuggestion> {
    const { columns, businessContext, columnNotes } = input;
    const warnings: string[] = [];
    const baseMetrics: BaseMetricDraft[] = [];

    // Edge case: no columns at all
    if (!columns || columns.length === 0) {
      warnings.push('No numeric columns detected');
      return { baseMetrics: [], derivedMetrics: [], warnings };
    }

    // Track revenue-like and cost-like and units-like metrics for derived proposals
    let revenueMetric: BaseMetricDraft | null = null;
    let costMetric: BaseMetricDraft | null = null;
    let unitsMetric: BaseMetricDraft | null = null;

    for (const col of columns) {
      // Skip non-numeric or identifier columns
      if (!isNumericColumn(col) || isIdentifierColumn(col)) continue;

      const note = columnNotes[col.columnName] || '';
      const agg = resolveAgg(col.columnName, note, businessContext);

      const metric: BaseMetricDraft = {
        metricKey: toSnakeCase(col.columnName),
        exprSql: col.columnName,
        agg,
        filterCondition: null,
      };
      baseMetrics.push(metric);

      // Units warning: suggest COUNT as alternative
      if (UNITS_KEYWORDS.test(`${note} ${col.columnName}`)) {
        warnings.push(`Proposed SUM for ${col.columnName} -- confirm vs COUNT`);
        if (!unitsMetric) unitsMetric = metric;
      }

      // Track first revenue-like and cost-like metrics
      if (!revenueMetric && matchesKeyword(REVENUE_KEYWORDS, col.columnName, note, businessContext)) {
        revenueMetric = metric;
      }
      if (!costMetric && matchesKeyword(COST_KEYWORDS, col.columnName, note, businessContext)) {
        costMetric = metric;
      }
      if (!unitsMetric && matchesKeyword(UNITS_KEYWORDS, col.columnName, note, businessContext)) {
        unitsMetric = metric;
      }
    }

    // No numeric non-id columns found
    if (baseMetrics.length === 0) {
      warnings.push('No numeric columns detected');
    }

    // ── Derived metric proposals ──
    const derivedMetrics: DerivedMetricDraft[] = [];

    // GP = revenue - cost (if both exist)
    if (revenueMetric && costMetric) {
      derivedMetrics.push({
        derivedMetricKey: 'GP',
        baseMetricsCsv: `${revenueMetric.metricKey},${costMetric.metricKey}`,
        exprSql: `${revenueMetric.exprSql} - ${costMetric.exprSql}`,
        description: 'Gross Profit',
      });
    }

    // ASP = revenue / units (if both exist, with divide-by-zero guard)
    if (revenueMetric && unitsMetric) {
      derivedMetrics.push({
        derivedMetricKey: 'ASP',
        baseMetricsCsv: `${revenueMetric.metricKey},${unitsMetric.metricKey}`,
        exprSql: `CASE WHEN ${unitsMetric.exprSql} = 0 OR ${unitsMetric.exprSql} IS NULL THEN NULL ELSE ${revenueMetric.exprSql} / ${unitsMetric.exprSql} END`,
        description: 'Average Selling Price',
      });
    }

    return { baseMetrics, derivedMetrics, warnings };
  },
};
